import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';

/**
 * List of sensitive routes/endpoints that should not be cached
 * These typically contain authentication data, personal information, or sensitive business data
 */
const SENSITIVE_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/callback',
  '/account',        // Covers: /account, /account/enterprise, /account/data_request, /account/profile, etc.
  '/csi/contracts', // CSI contract routes
];

/**
 * Checks if the current page route matches any sensitive path pattern
 * This checks window.location.pathname (the page route) rather than the API endpoint URL,
 * because we want to prevent caching of API responses when the user is on a sensitive page.
 */
function isSensitivePath(): boolean {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }
  const currentPagePath = window.location.pathname.toLowerCase();
  return SENSITIVE_PATHS.some((sensitivePath) => currentPagePath.includes(sensitivePath.toLowerCase()));
}

/**
 * No-Cache Interceptor
 * 
 * Security: Disables client-side caching on sensitive pages/endpoints
 * 
 * This interceptor adds cache-control headers to HTTP responses for sensitive routes
 * to prevent browsers and proxies from caching sensitive data.
 * 
 * Headers added:
 * - Cache-Control: no-store, no-cache, must-revalidate, private
 * - Pragma: no-cache
 * - Expires: 0
 */
export const NoCacheInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    map((event) => {
      // Only process successful HTTP responses
      if (!(event instanceof HttpResponse)) {
        return event;
      }

      // Check if user is currently on a sensitive page
      // We check the page route (window.location) rather than API endpoint URL,
      // because we want to prevent caching of ALL API responses when on sensitive pages
      const isSensitive = isSensitivePath();
      
      // Enhanced debug logging to help troubleshoot
      // Note: Interceptor headers won't show in DevTools Network tab (shows original server headers)
      // But they are applied to the response object that Angular uses internally
      if (typeof console !== 'undefined') {
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
        // console.log('[NoCacheInterceptor]', {
        //   currentPage: currentPath,
        //   isSensitive,
        //   apiUrl: req.url,
        //   willAddHeaders: isSensitive,
        //   note: 'Headers added to response object (not visible in DevTools Network tab)'
        // });
      }

      if (isSensitive) {
        // Add cache-control headers to prevent caching
        // Note: These headers are added to the response object, but browsers may not
        // respect them for responses from interceptors. The primary protection comes
        // from meta tags in HTML and service worker configuration.
        const headers = event.headers
          .set('Cache-Control', 'no-store, no-cache, must-revalidate, private')
          .set('Pragma', 'no-cache')
          .set('Expires', '0');

        // Clone response with new headers
        const modifiedResponse = event.clone({ headers });

        // Debug logging - verify headers were added
        if (typeof console !== 'undefined') {
          console.log('[NoCacheInterceptor] ✓ Applied no-cache headers to response object');
          console.log('  Page:', window.location.pathname);
          console.log('  API:', req.url);
          console.log('  Headers in response object:', {
            'Cache-Control': modifiedResponse.headers.get('Cache-Control'),
            'Pragma': modifiedResponse.headers.get('Pragma'),
            'Expires': modifiedResponse.headers.get('Expires')
          });
          console.log('  ⚠️  Note: These headers are in Angular response object, NOT in actual HTTP response');
          console.log('  ⚠️  DevTools Network tab shows original server headers only');
          console.log('  ✅  Real protection comes from: meta tags + service worker config');
        }

        return modifiedResponse;
      }

      return event;
    })
  );
};
